import { useEffect, useState } from 'react';
import type { EntryDto, Puzzle } from '../api/types';
import { formatOwnShareText, formatShareText } from '../lib/shareText';

type Kind = 'team' | 'own';

interface TeamProps {
  kind?: 'team';
  puzzle: Puzzle;
  entries: EntryDto[];
  /** Namen der Spieler, die noch nicht getippt haben. Nur beim Vorab-
   *  Teilen (Zwischenstand) gesetzt — landet als Hinweis im Share-Text. */
  missingPlayers?: string[];
}

interface OwnProps {
  kind: 'own';
  puzzle: Puzzle;
  entry: EntryDto;
}

type Props = TeamProps | OwnProps;

export function ShareButton(props: Props) {
  const text = buildText(props);
  const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;

  const [copied, setCopied] = useState(false);
  const [showText, setShowText] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(t);
  }, [copied]);

  const onCopy = async (): Promise<void> => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback für ältere Browser
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
    } catch {
      setCopied(false);
      setShowText(true);
    }
  };

  return (
    <div className="share">
      <div className="share__buttons">
        <button type="button" className="button button--primary" onClick={onCopy}>
          {copied ? 'Kopiert ✔' : 'Text kopieren'}
        </button>
        <a
          className="button button--ghost"
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          WhatsApp
        </a>
      </div>
      <button
        type="button"
        className="share__toggle"
        onClick={() => setShowText((v) => !v)}
      >
        {showText ? 'Vorschau ausblenden' : 'Vorschau anzeigen'}
      </button>
      {showText && (
        <pre className="share__preview" aria-label="Share-Text Vorschau">
          {text}
        </pre>
      )}
    </div>
  );
}

function buildText(props: Props): string {
  const kind: Kind = props.kind ?? 'team';
  if (kind === 'own') {
    const own = props as OwnProps;
    return formatOwnShareText(own.puzzle, own.entry);
  }
  const team = props as TeamProps;
  return formatShareText(team.puzzle, team.entries, team.missingPlayers ?? []);
}
